/**
 * Phase 8 — PDF Layout Customization page.
 *
 * Lets the user pick a hex color per canonical section and reorder the
 * sections within the per-day PDF render loop. No new dependencies —
 * native `<input type="color">` for the picker and small ↑/↓ buttons
 * for ordering.
 *
 *  Save              → PUT /api/settings/pdf-layout
 *  Reset to Defaults → restore the canonical defaults (does NOT auto-save).
 *
 * The 7 canonical section keys + their friendly labels live in
 * `@/lib/settingsApi`. The backend rejects any payload missing/adding a
 * key, so we mirror the same contract here.
 */
import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, Save, RotateCcw, ArrowUp, ArrowDown, Palette } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  getPdfLayout,
  updatePdfLayout,
  PDF_LAYOUT_SECTION_KEYS,
  PDF_LAYOUT_SECTION_LABELS,
  DEFAULT_PDF_LAYOUT,
} from "@/lib/settingsApi";

const _hexEq = (a, b) => String(a || "").toLowerCase() === String(b || "").toLowerCase();

function _layoutsEqual(a, b) {
  if (!a || !b) return false;
  // Orders must match positionally.
  if (a.section_order.length !== b.section_order.length) return false;
  for (let i = 0; i < a.section_order.length; i += 1) {
    if (a.section_order[i] !== b.section_order[i]) return false;
  }
  for (const k of PDF_LAYOUT_SECTION_KEYS) {
    if (!_hexEq(a.section_colors[k], b.section_colors[k])) return false;
  }
  // Phase 8 add-on — pastry accent is part of the comparison so the
  // Save and Reset buttons enable/disable on changes to it.
  if (!_hexEq(a.pastry_accent, b.pastry_accent)) return false;
  return true;
}

export default function PdfLayoutSettings() {
  const [layout, setLayout] = useState(null); // {section_colors, section_order}
  const [baseline, setBaseline] = useState(null); // last-saved snapshot, for "dirty" detection
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dirty = useMemo(() => layout && baseline && !_layoutsEqual(layout, baseline), [layout, baseline]);
  const matchesDefaults = useMemo(
    () => layout && _layoutsEqual(layout, DEFAULT_PDF_LAYOUT),
    [layout],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPdfLayout()
      .then((data) => {
        if (cancelled) return;
        const snap = {
          section_colors: { ...data.section_colors },
          section_order: [...data.section_order],
          pastry_accent: data.pastry_accent || DEFAULT_PDF_LAYOUT.pastry_accent,
        };
        setLayout(snap);
        setBaseline({
          section_colors: { ...snap.section_colors },
          section_order: [...snap.section_order],
          pastry_accent: snap.pastry_accent,
        });
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message || "Failed to load PDF layout");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleColorChange = (key, value) => {
    setLayout((prev) => ({
      ...prev,
      section_colors: { ...prev.section_colors, [key]: value },
    }));
  };

  const handlePastryChange = (value) => {
    setLayout((prev) => ({ ...prev, pastry_accent: value }));
  };

  const moveSection = (idx, delta) => {
    setLayout((prev) => {
      const order = [...prev.section_order];
      const next = idx + delta;
      if (next < 0 || next >= order.length) return prev;
      [order[idx], order[next]] = [order[next], order[idx]];
      return { ...prev, section_order: order };
    });
  };

  const handleSave = async () => {
    if (!layout) return;
    setSaving(true);
    try {
      const saved = await updatePdfLayout({
        section_colors: layout.section_colors,
        section_order: layout.section_order,
        pastry_accent: layout.pastry_accent,
      });
      const snap = {
        section_colors: { ...saved.section_colors },
        section_order: [...saved.section_order],
        pastry_accent: saved.pastry_accent || DEFAULT_PDF_LAYOUT.pastry_accent,
      };
      setLayout(snap);
      setBaseline({
        section_colors: { ...snap.section_colors },
        section_order: [...snap.section_order],
        pastry_accent: snap.pastry_accent,
      });
      toast({ title: "PDF layout saved", description: "New colours and ordering will be applied on the next export." });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Failed to save";
      toast({ title: "Save failed", description: String(msg), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    setLayout({
      section_colors: { ...DEFAULT_PDF_LAYOUT.section_colors },
      section_order: [...DEFAULT_PDF_LAYOUT.section_order],
      pastry_accent: DEFAULT_PDF_LAYOUT.pastry_accent,
    });
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-6" data-testid="pdf-layout-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="pdf-layout-error">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Couldn't load PDF layout</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl p-6" data-testid="pdf-layout-page">
      <div className="mb-6 flex items-center gap-2">
        <Palette className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold tracking-tight" data-testid="pdf-layout-title">
          PDF Layout
        </h1>
      </div>

      <p className="mb-6 text-sm text-muted-foreground">
        Customize the section bar colours and the order in which sections appear on each day of
        the per-day voyage PDF.
      </p>

      {/* Colours card */}
      <Card className="mb-6" data-testid="pdf-layout-colors-card">
        <CardHeader>
          <CardTitle className="text-base">Section colours</CardTitle>
          <CardDescription>
            Applies to the bar above each section. Chef's Special controls the per-restaurant pill.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3">
            {PDF_LAYOUT_SECTION_KEYS.map((key) => (
              <div
                key={key}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
                data-testid={`pdf-layout-color-row-${key}`}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium" data-testid={`pdf-layout-color-label-${key}`}>
                    {PDF_LAYOUT_SECTION_LABELS[key]}
                  </div>
                  <div className="font-mono text-xs text-muted-foreground" data-testid={`pdf-layout-color-hex-${key}`}>
                    {layout.section_colors[key]}
                  </div>
                </div>
                <input
                  type="color"
                  value={layout.section_colors[key]}
                  onChange={(e) => handleColorChange(key, e.target.value)}
                  className="h-9 w-14 cursor-pointer rounded border bg-transparent p-1"
                  aria-label={`Color for ${PDF_LAYOUT_SECTION_LABELS[key]}`}
                  data-testid={`pdf-layout-color-input-${key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pastry accent — Phase 8 add-on. Separate from the 7-key
          section colours because it's an accent applied to the
          buffet/crew-mess PASTRY column tint, not a section bar. */}
      <Card className="mb-6" data-testid="pdf-layout-pastry-card">
        <CardHeader>
          <CardTitle className="text-base">Pastry Accent</CardTitle>
          <CardDescription>
            Tints the PASTRY column header + body cells in every buffet and crew-mess meal block.
            This is an accent, not a section — it doesn't appear in the section order list.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="flex items-center justify-between gap-3 rounded-md border p-3"
            data-testid="pdf-layout-pastry-row"
          >
            <div className="flex-1">
              <div className="text-sm font-medium" data-testid="pdf-layout-pastry-label">
                Pastry Accent
              </div>
              <div className="font-mono text-xs text-muted-foreground" data-testid="pdf-layout-pastry-hex">
                {layout.pastry_accent}
              </div>
            </div>
            <input
              type="color"
              value={layout.pastry_accent}
              onChange={(e) => handlePastryChange(e.target.value)}
              className="h-9 w-14 cursor-pointer rounded border bg-transparent p-1"
              aria-label="Pastry accent colour"
              data-testid="pdf-layout-pastry-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Order card */}
      <Card className="mb-6" data-testid="pdf-layout-order-card">
        <CardHeader>
          <CardTitle className="text-base">Section order</CardTitle>
          <CardDescription>
            Sections render in this order on every day. Chef's Special is rendered inside each
            restaurant row today — re-ordering its slot has no visual effect yet, but the slot
            stays in the contract.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2" data-testid="pdf-layout-order-list">
            {layout.section_order.map((key, idx) => (
              <li
                key={key}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
                data-testid={`pdf-layout-order-row-${key}`}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium" data-testid={`pdf-layout-order-label-${key}`}>
                    {PDF_LAYOUT_SECTION_LABELS[key]}
                  </span>
                  <span
                    className="ml-2 inline-block h-3 w-6 rounded-sm border"
                    style={{ backgroundColor: layout.section_colors[key] }}
                    aria-hidden="true"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={idx === 0}
                    onClick={() => moveSection(idx, -1)}
                    aria-label={`Move ${PDF_LAYOUT_SECTION_LABELS[key]} up`}
                    data-testid={`pdf-layout-order-up-${key}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={idx === layout.section_order.length - 1}
                    onClick={() => moveSection(idx, 1)}
                    aria-label={`Move ${PDF_LAYOUT_SECTION_LABELS[key]} down`}
                    data-testid={`pdf-layout-order-down-${key}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Separator className="my-4" />

      <div className="flex items-center justify-end gap-2" data-testid="pdf-layout-actions">
        <Button
          type="button"
          variant="outline"
          onClick={handleResetToDefaults}
          disabled={saving || matchesDefaults}
          data-testid="pdf-layout-reset-btn"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="pdf-layout-save-btn"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save
        </Button>
      </div>
    </div>
  );
}
